/**
 * ExecutorDashboard — top-level shell for users with role="executor".
 *
 * Routing:
 *   /executor/:clientId/progress     → ExecutorProgressView (pre-death, tier-gated view)
 *   /executor/:clientId/report-death → ReportDeathPage
 *   /executor/:clientId/waiting      → GracePeriodWaiting
 *   /                                → full estate read-only (when death is verified)
 *   /category/:slug, /export/*, etc  → full estate routes
 *
 * s127 fix: the full estate unlocks on VERIFIED DEATH alone (two-gate design) — sharing_tier
 * gates the PRE-death view only, never the post-death settlement. See lib/executor-access.ts.
 */

import { SignOutButton } from "@/components/SignOutButton";
import { useGuardedSignOut } from "@/components/LogoutSaveCheck";
import { useState, useEffect } from "react";
import { Switch, Route, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiRequest, GATING_QUERY_OPTIONS } from "@/lib/queryClient";
import { useDeathEvent } from "@/lib/use-death-event";
import { canAccessFullEstate } from "@/lib/executor-access";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ExecutorSidebar } from "@/components/ExecutorSidebar";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import Dashboard from "@/pages/Dashboard";
import GenericCategoryPage from "@/pages/GenericCategoryPage";
import PasswordTablePage from "@/pages/PasswordTablePage";
import OntarioFormsExportPage from "@/pages/OntarioFormsExportPage";
import ExecutorProgressView from "@/pages/ExecutorProgressView";
import ReportDeathPage from "@/pages/ReportDeathPage";
import GracePeriodWaiting from "@/pages/GracePeriodWaiting";
import ExecutorPostDeathDashboard from "@/pages/ExecutorPostDeathDashboard";
import ExecutorEstateView from "@/pages/ExecutorEstateView";
import LetterGenerator from "@/pages/LetterGenerator";
import LetterPreviewPage from "@/pages/LetterPreviewPage";
import StatementScannerPage from "@/pages/StatementScannerPage";
import EstateSummaryPage from "@/pages/EstateSummaryPage";
import ExecutorRiskProfilePage from "@/pages/ExecutorRiskProfilePage";
import EstateDocumentsPage from "@/pages/EstateDocumentsPage";
import ExecutorTaskList from "@/pages/executor/ExecutorTaskList";
import ExecutorOnboardingPage, { DeclinedEndCard, useExecutorOnboarding, type DeclineResult } from "@/pages/executor/ExecutorOnboardingPage";
import { ExecutorNotesWidget } from "@/components/executor/ExecutorNotesWidget";
import { WherePage } from "@/components/plan/WherePage";
import { ContactsPage } from "@/components/plan/ContactsPage";

/** s237: the planner's first name, as the executor's pages say it ("Reade did not say"). Pure. */
const firstWord = (name: string | null | undefined) => (name ?? "").trim().split(/\s+/)[0] ?? "";
import NotFound from "@/pages/not-found";
import { useAuth } from "@/contexts/AuthContext";

function ExecutorRootRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/dashboard"); }, []);
  return null;
}
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Eye } from "lucide-react";
import type { Client } from "@shared/schema";
import { UserMenu } from "@/components/UserMenu";
import { Footer } from "@/components/Footer";

export function ExecutorDashboard() {
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // §1.6 Build 2: the task list is the executor's workspace ("Estate"); the plan views are
  // "Plan". Post-death the plan is editable in place (EXECUTOR-WRITE-1 Phase 4R, s144), so the
  // former "(Read-Only)" qualifier was dropped — this whole view only mounts once death is verified.
  const onTasksPage = location.endsWith("/tasks");

  // Fetch assigned clients. User-scope the key (Issue B, s84) so a session swap
  // can't surface the previous user's cached clients. Invalidations stay bare.
  // Explicit queryFn REQUIRED — the default derives the URL from queryKey.join("/"),
  // which would turn a 2-element key into /api/clients/<userId> (the by-id endpoint)
  // instead of the list. The key scopes the cache; the queryFn pins the URL.
  const { data: clients, isError: clientsError, status: clientsStatus } = useQuery<Client[]>({
    queryKey: ["/api/clients", user?.id],
    queryFn: async () => (await apiRequest("GET", "/api/clients")).json(),
    ...GATING_QUERY_OPTIONS,
  });
  const clientData = clients && clients.length > 0 ? clients[0] : null;
  const clientId = clientData?.id ?? "";

  // Fetch this user's executor access record. FOURTH gating read (s163): its result feeds
  // sharingTier, which gates the pre-death view. `?? 1` used to make a FAILED read
  // indistinguishable from a real tier-1 grant — a permissive default reached by an error path,
  // which is the same class of dishonesty as the swallowed death-event. Unresolved is not tier 1.
  const {
    data: executorAccesses,
    isError: executorAccessError,
    status: executorAccessStatus,
  } = useQuery<any[]>({
    queryKey: ["/api/clients", clientId, "executors"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/executors`);
      if (!res.ok) throw new Error(`${res.status}: Failed to fetch executors`);
      return res.json();
    },
    enabled: !!clientId,
    ...GATING_QUERY_OPTIONS,
  });
  const myAccess = executorAccesses?.find((e: any) => e.userId === user?.id);
  // D-217.14 (s218, access-spec R4): a row with no tier is None, not Overview.
  const sharingTier: number = myAccess?.sharingTier ?? 0;

  // Fetch active death event. LOGIN-RELIABILITY-1 (s163): this read decides which of two
  // fundamentally different views an executor sees, so a failure must NOT resolve as "alive".
  const { data: deathEvent, isError: deathEventError, status: deathEventStatus } =
    useDeathEvent(clientId);

  // 🔴 LOADING-STATE-HONESTY-2 (s163, pulled forward — load-bearing, not polish).
  //
  // `deathVerified` is FALSE for an unresolved query just as surely as for a living planner,
  // because `undefined?.status !== "verified"`. Reading a committed view off that is the defect
  // this whole arc exists to remove: it renders "this person is alive" from an answer nobody
  // received. An error branch cannot cover it, because a paused query never reaches `isError`.
  //
  // So the gate is RESOLUTION, not error. A query is resolved when it has succeeded or failed —
  // 'pending' covers in-flight, retrying, paused, and not-yet-enabled alike, and every one of
  // those means we do not know yet. Downstream reads of `deathEvent` / `myAccess` / `clients`
  // only run once their query has actually answered.
  const gatingResolved =
    clientsStatus !== "pending" &&
    (clientsError || clients?.length === 0 || (executorAccessStatus !== "pending" && deathEventStatus !== "pending"));

  const deathVerified = deathEvent?.status === "verified";
  // s127 fix: post-death, the full estate unlocks on verification ALONE — a pre-death
  // "Progress only" tier must not strand the settlement. sharing_tier gates the pre-death
  // view only (see the tier-0 guard below + ExecutorProgressView).
  const showFullEstate = canAccessFullEstate(deathVerified);

  // s231 EXECUTOR-ONBOARDING (D-231.20): "Before you start" comes first after the death is verified.
  // Until question 1 and the two death answers are in, every full-estate route shows it instead.
  // A confirmed No closes the plan to this person, so the end card is held HERE (the server now
  // refuses their reads) and rendered on its own.
  const onboarding = useExecutorOnboarding(clientId, gatingResolved && showFullEstate);
  const [justDeclined, setDeclined] = useState<(DeclineResult & { clientId: string }) | null>(null);
  // s231 ruling 3 (D-231.54): after a reload the declined executor has no accepted estate, so the
  // clients list comes back empty. Before the generic "No access granted" screen, ask whether they
  // declined an estate: if so, the end card shows again (read-only, Form 74G behind its ack).
  const noAcceptedEstate = clientsStatus === "success" && (clients?.length ?? 0) === 0;
  const declinedLookup = useQuery<{ declined: (DeclineResult & { clientId: string }) | null }>({
    queryKey: ["/api/executor/declined", user?.id],
    queryFn: async () => {
      const res = await fetch("/api/executor/declined", { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: declined`);
      return res.json();
    },
    enabled: noAcceptedEstate,
    retry: false,
  });
  const declined = justDeclined ?? declinedLookup.data?.declined ?? null;

  // Auto-open password change dialog
  useEffect(() => {
    if (user?.mustChangePassword) setChangePasswordOpen(true);
  }, [user?.mustChangePassword]);

  // s227 (LOGOUT-SAVE-CHECK-1): every sign-out goes through the one check.
  const guardedSignOut = useGuardedSignOut();
  const handleLogout = () => guardedSignOut(async () => {
    try {
      await logout();
      toast({ title: "Logged out", description: "You have been successfully logged out." });
    } catch (error: any) {
      toast({ title: "Logout failed", description: error.message, variant: "destructive" });
    }
  });

  // s231: the plan is closed to an executor who just declined — only the end card remains. Placed
  // BEFORE every gating branch: once declined, the clients / access reads answer differently.
  if (declined) {
    return (
      <div className="flex flex-col min-h-screen">
        <header className="flex items-center justify-between p-4 shrink-0 nav-header">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-semibold">{user?.fullName}</span>
          </div>
          <div className="flex items-center gap-2">
            <SignOutButton />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto flex justify-center px-4 sm:px-9 py-7">
          <DeclinedEndCard clientId={declined.clientId} result={declined} />
        </main>
        <Footer />
      </div>
    );
  }

  // Gating-query failure. Distinct from BOTH neighbouring states: `clients === undefined` means
  // the fetch failed or is still running — it is NOT `length === 0` ("no access granted"), and it
  // must not fall through to the catch-all "Loading…", which never ends.
  if (clientsError || deathEventError || executorAccessError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-sm" data-testid="executor-gating-error">
          <h2 className="text-2xl font-semibold mb-2">We couldn't load this estate's status</h2>
          <p className="text-muted-foreground mb-4">
            Something went wrong on our end — not with your access. Please try again.
          </p>
          <Button onClick={() => window.location.reload()} data-testid="button-retry-gating">
            Try again
          </Button>
        </div>
      </div>
    );
  }

  // Neutral shell — the gating reads have not answered yet (LOADING-STATE-HONESTY-2).
  //
  // Placed AFTER the error branch and BEFORE every committed view, so there is no ordering by
  // which an unresolved query can reach a view that states something about this estate. The copy
  // reports only what is actually happening and promises no outcome: the previous
  // "Opening the estate / One moment" asserted success it could not know, which is the same class
  // of dishonesty as rendering the pre-death view off an unanswered query — it just fails slower.
  if (!gatingResolved) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center" data-testid="executor-gating-pending">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking this estate's status…</p>
        </div>
      </div>
    );
  }

  // s231: still asking whether this executor declined an estate — say nothing about access yet.
  if (noAcceptedEstate && declinedLookup.status === "pending") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center" data-testid="executor-gating-pending">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Checking this estate's status…</p>
        </div>
      </div>
    );
  }

  // No client assigned
  if (clients && clients.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">No access granted</h2>
          <p className="text-muted-foreground">You do not have access to any estate planning data.</p>
          <p className="text-muted-foreground">Please contact the client or their financial advisor.</p>
        </div>
      </div>
    );
  }

  // Tier 0 — explicitly no access
  if (myAccess && sharingTier === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-sm">
          <h2 className="text-2xl font-semibold mb-2">Access not yet granted</h2>
          <p className="text-muted-foreground">
            You have been named as an executor for {clientData?.name}, but access has not been enabled
            yet. Please contact their advisor for more information.
          </p>
        </div>
      </div>
    );
  }


  // Full estate view (tier 2 + death verified)
  if (showFullEstate && clientData) {
    const onboardingDue = onboarding.data?.done === false;
    return (
      <div className="flex h-screen w-full">
        <ExecutorSidebar
          clientName={clientData.name}
          clientId={clientData.id}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between p-4 shrink-0 nav-header">
            <div className="flex items-center gap-3">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-muted-foreground" />
                <div>
                  <h1 className="text-lg font-semibold">{user?.fullName}</h1>
                  <p className="text-sm text-muted-foreground">Executor — {clientData.name}'s {onTasksPage ? "Estate" : "Plan"}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SignOutButton />
              <UserMenu
                onLogout={handleLogout}
                onChangePassword={() => setChangePasswordOpen(true)}
                userName={user?.fullName}
              />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto overflow-x-hidden">
            {onboarding.status === "pending" ? (
              <div className="flex items-center justify-center p-12" data-testid="executor-onboarding-pending">
                <p className="text-muted-foreground">Checking this estate&rsquo;s status…</p>
              </div>
            ) : onboardingDue && onboarding.data ? (
              <ExecutorOnboardingPage clientId={clientData.id} state={onboarding.data} onDeclined={(r) => setDeclined({ ...r, clientId: clientData.id })} />
            ) : (
            <Switch>
              <Route path="/executor/:clientId/start">
                {(params) => onboarding.data
                  ? <ExecutorOnboardingPage clientId={params.clientId} state={onboarding.data} onDeclined={(r) => setDeclined({ ...r, clientId: clientData.id })} />
                  : <NotFound />}
              </Route>
              <Route path="/executor/:clientId/tasks">{(params) => <ExecutorTaskList clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/dashboard">{(params) => <ExecutorPostDeathDashboard clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/estate">{(params) => <ExecutorEstateView clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/letters/:instanceKey">{(params) => <LetterPreviewPage clientId={params.clientId} instanceKey={decodeURIComponent(params.instanceKey)} />}</Route>
              <Route path="/executor/:clientId/letters">{(params) => <LetterGenerator clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/statement-scanner">{(params) => <StatementScannerPage clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/estate-summary">{(params) => <EstateSummaryPage clientId={params.clientId} isExecutor={true} />}</Route>
              <Route path="/executor/:clientId/risk-profile">{(params) => <ExecutorRiskProfilePage clientId={params.clientId} />}</Route>
              <Route path="/executor/:clientId/estate-documents">{(params) => <EstateDocumentsPage clientId={params.clientId} />}</Route>
              <Route path="/dashboard" component={() => <ExecutorPostDeathDashboard clientId={clientId} />} />
              <Route path="/category/:slug">{(params) => <GenericCategoryPage clientData={clientData} categorySlug={params.slug} readOnly />}</Route>
              {/* s237 PR D part 1 (D-236.50-25(b)): Where things are — the planner's own page, the executor's view of it. */}
              <Route path="/executor/:clientId/where">{(params) => <WherePage clientId={params.clientId} onOpen={() => {}}
                executor={{ planner: firstWord(onboarding.data?.plannerName ?? clientData.name) }} />}</Route>
              {/* s237 PR D part 2 (D-236.50-25(b), D-237.35): Contacts — the planner's own page, the executor's view of it. */}
              <Route path="/executor/:clientId/contacts">{(params) => <ContactsPage clientId={params.clientId} onOpen={() => {}}
                executor={{ planner: firstWord(onboarding.data?.plannerName ?? clientData.name) }} />}</Route>
              <Route path="/export/ontario-forms" component={() => <OntarioFormsExportPage clientData={clientData} />} />
              <Route path="/executor" component={ExecutorRootRedirect} />
              <Route component={NotFound} />
            </Switch>
            )}
          </main>
          <Footer />
        </div>
        {/* Estate-wide notes widget (§1.6, D-123.16) — full-estate settlement surface only. */}
        <ExecutorNotesWidget clientId={clientData.id} />
        <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} mustChange={user?.mustChangePassword} />
      </div>
    );
  }

  // Limited view: progress, report death, grace period waiting
  return (
    <div className="flex flex-col min-h-screen">
      <header className="flex items-center justify-between p-4 shrink-0 nav-header">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5" />
          <span className="font-semibold">{user?.fullName}</span>
          {clientData && (
            <span className="text-sm text-white/70">— Executor for {clientData.name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SignOutButton />
          <UserMenu
            onLogout={handleLogout}
            onChangePassword={() => setChangePasswordOpen(true)}
            userName={user?.fullName}
          />
        </div>
      </header>
      <main className="flex-1 overflow-y-auto">
        <Switch>
          <Route path="/executor/:clientId/progress">{(params) => <ExecutorProgressView clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/report-death">{(params) => <ReportDeathPage clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/waiting">{(params) => <GracePeriodWaiting clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/dashboard">{(params) => <ExecutorPostDeathDashboard clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/estate">{(params) => <ExecutorEstateView clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/letters/:instanceKey">{(params) => <LetterPreviewPage clientId={params.clientId} instanceKey={decodeURIComponent(params.instanceKey)} />}</Route>
          <Route path="/executor/:clientId/letters">{(params) => <LetterGenerator clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/statement-scanner">{(params) => <StatementScannerPage clientId={params.clientId} />}</Route>
          <Route path="/executor/:clientId/estate-summary">{(params) => <EstateSummaryPage clientId={params.clientId} isExecutor={true} />}</Route>
          <Route path="/executor/:clientId/risk-profile">{(params) => <ExecutorRiskProfilePage clientId={params.clientId} />}</Route>
          <Route>
            {clientId ? (
              <ExecutorProgressView clientId={clientId} />
            ) : (
              <div className="flex items-center justify-center p-12">
                <p className="text-muted-foreground">Loading…</p>
              </div>
            )}
          </Route>
        </Switch>
      </main>
      <Footer />
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} mustChange={user?.mustChangePassword} />
    </div>
  );
}
